import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import api from "../../../config/URL";
import "../../../styles/admin.css";
import { FiAlertTriangle } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

function AffiliatePayoutAdd() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const validationSchema = Yup.object({
    user_id: Yup.string().required("*Select a user"),
    status: Yup.string().required("*Select a status"),
    requested_at: Yup.string().required("*Select a requested date"),
    paid_at: Yup.string().required("*Select a paid date"),
    payment_mode: Yup.string().required("*Select a payment"),
    amount: Yup.number()
      .typeError("*Amount must be a number")
      .required("*Amount is required"),
  });

  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? new Date() : date;
  };

  const formik = useFormik({
    initialValues: {
      user_id: "",
      amount: "",
      status: "",
      requested_at: new Date().toISOString().split("T")[0], // Current date in YYYY-MM-DD format
      paid_at: new Date().toISOString().split("T")[0],
      payment_mode: "",
      transaction_id: "",
      attachment: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("user_id", values.user_id);
      formData.append("amount", values.amount);
      formData.append("status", values.status);
      formData.append("requested_at", values.requested_at);
      formData.append("paid_at", values.paid_at);
      formData.append("payment_mode", values.payment_mode);
      formData.append("transaction_id", values.transaction_id);

      if (values.attachment) {
        formData.append("attachment", values.attachment);
      }

      if (values.attachment) {
        const allowedTypes = [
          "image/png",
          "image/jpeg",
          "image/jpg",
          "application/pdf",
        ];
        if (!allowedTypes.includes(values.attachment.type)) {
          toast.error("Only PNG, JPG, and PDF files are allowed");
          return;
        }
      }

      setLoadIndicator(true);
      try {
        const response = await api.post(
          `admin/affiliate-payout/create`,
          formData
        );
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          navigate("/affiliate-payout");
        } else if (response.status === 422) {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (error.response) {
          const { status, data } = error.response;

          if (status === 422 && data.errors) {
            Object.keys(data.errors).forEach((key) => {
              data.errors[key].forEach((msg) => {
                toast.error(msg, {
                  icon: <FiAlertTriangle className="text-warning" />,
                });
              });
            });
          } else if (data.message) {
            toast.error(data.message, {
              icon: <FiAlertTriangle className="text-danger" />,
            });
          } else {
            toast.error("An unexpected error occurred.", {
              icon: <FiAlertTriangle className="text-danger" />,
            });
          }
        } else {
          toast.error("Network or server error.", {
            icon: <FiAlertTriangle className="text-danger" />,
          });
        }
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await api.get(`admin/affiliate/users`);
        setData(response.data.data);
      } catch (error) {
        toast.error("Error Fetching Data");
      }
    };
    getData();
  }, []);

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        {loading ? (
          <div className="loader-container">
            <div className="loader">
              <svg viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32"></circle>
              </svg>
            </div>
          </div>
        ) : (
          <>
            <div className="card shadow border-0 mb-3">
              <div className="row p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h1 className="h4 ls-tight">Add Affiliate</h1>
                  <Link to="/affiliate-payout">
                    <button type="button" className="btn btn-light btn-sm">
                      <span>Back</span>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            <div
              className="container card shadow border-0"
              style={{ minHeight: "60vh" }}
            >
              <div className="row mt-3 mb-5 p-3">
                {/* User ID */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    User ID<span className="text-danger">*</span>
                  </label>
                  <select
                    name="user_id"
                    className={`form-select ${
                      formik.touched.user_id && formik.errors.user_id
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.user_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">-- Select a user --</option>
                    {data.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.user_id && formik.errors.user_id && (
                    <div className="invalid-feedback">
                      {formik.errors.user_id}
                    </div>
                  )}
                </div>

                {/* Amount */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="amount"
                    className={`form-control ${
                      formik.touched.amount && formik.errors.amount
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter amount"
                  />
                  {formik.touched.amount && formik.errors.amount && (
                    <div className="invalid-feedback">
                      {formik.errors.amount}
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Status<span className="text-danger">*</span>
                  </label>
                  <select
                    name="status"
                    className={`form-select ${
                      formik.touched.status && formik.errors.status
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value=""></option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  {formik.touched.status && formik.errors.status && (
                    <div className="invalid-feedback">
                      {formik.errors.status}
                    </div>
                  )}
                </div>

                {/* Payment Mode */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Payment Mode<span className="text-danger">*</span>
                  </label>
                  <select
                    name="payment_mode"
                    className={`form-select ${
                      formik.touched.payment_mode && formik.errors.payment_mode
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.payment_mode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value=""></option>
                    <option value="UPI">UPI</option>
                    <option value="Net Banking">Net Banking</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Direct Banking">Direct Banking</option>
                  </select>
                  {formik.touched.payment_mode &&
                    formik.errors.payment_mode && (
                      <div className="invalid-feedback">
                        {formik.errors.payment_mode}
                      </div>
                    )}
                </div>

                {/* Requested At Date */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Requested At<span className="text-danger">*</span>
                  </label>
                  <div className="custom-date-picker">
                    <DatePicker
                      selected={parseDate(formik.values.requested_at)}
                      onChange={(date) => {
                        formik.setFieldValue(
                          "requested_at",
                          date ? date.toISOString().split("T")[0] : ""
                        );
                      }}
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                      className={`form-control ${
                        formik.touched.requested_at &&
                        formik.errors.requested_at
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>
                </div>

                {/* Paid At Date */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Paid At<span className="text-danger">*</span>
                  </label>
                  <div className="custom-date-picker">
                    <DatePicker
                      selected={parseDate(formik.values.paid_at)}
                      onChange={(date) => {
                        formik.setFieldValue(
                          "paid_at",
                          date ? date.toISOString().split("T")[0] : ""
                        );
                      }}
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                      className={`form-control ${
                        formik.touched.paid_at && formik.errors.paid_at
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>
                </div>

                {/* Transaction ID*/}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Transaction ID (Optional)
                  </label>
                  <input
                    type="text"
                    name="transaction_id"
                    className={`form-control ${
                      formik.touched.transaction_id &&
                      formik.errors.transaction_id
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.transaction_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter transaction ID"
                  />
                </div>

                {/* Attachment*/}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">Attachment (Optional)</label>
                  <input
                    type="file"
                    name="attachment"
                    className={`form-control ${
                      formik.touched.attachment && formik.errors.attachment
                        ? "is-invalid"
                        : ""
                    }`}
                    onChange={(event) => {
                      formik.setFieldValue(
                        "attachment",
                        event.currentTarget.files[0]
                      );
                    }}
                    onBlur={formik.handleBlur}
                    accept=".png,.jpg,.jpeg,.pdf"
                  />
                  <p style={{ fontSize: "13px" }}>
                    Note: Maximum file size is 2MB. Allowed: .png, .jpg, .pdf.
                  </p>
                </div>
              </div>

              {/* Submit */}
              <div className="hstack p-2 mt-5">
                <button
                  type="submit"
                  className="btn btn-sm btn-button"
                  disabled={loadIndicator}
                >
                  {loadIndicator && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                  )}
                  Add Affiliate Payout
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </section>
  );
}

export default AffiliatePayoutAdd;
